import { useEffect, useRef, useState } from "react";

/**
 * InferredValueIndicator — subtle visual hint shown next to an Estimated Value
 * badge when the displayed amount is being inferred from cost (no explicit
 * estimate set).  Wraps its children so the dashed-underline styling applies
 * to the actual badge content.  On hover / focus / tap shows an inline
 * tooltip with the explanatory copy.  Mobile-friendly (touch toggles open),
 * keyboard-accessible (tab focuses, Escape closes), and screen-reader friendly
 * via aria-label.
 *
 * Renders children unchanged when `inferred` is false, so consumers can wrap
 * unconditionally:
 *   <InferredValueIndicator inferred={isValueInferred(item)}>{badge}</InferredValueIndicator>
 */
const COPY = "Estimated value inferred from cost because no explicit estimate set.";

export default function InferredValueIndicator({
  inferred,
  children,
  testid = "id-pricing-value-inferred",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside tap / Escape — important for the mobile-tap-to-reveal flow.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!inferred) return children;

  return (
    <span
      ref={ref}
      data-testid={testid}
      role="note"
      aria-label="Estimated value inferred"
      tabIndex={0}
      title={COPY}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); } }}
      onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.focus(); }}
      className="relative inline-flex items-baseline gap-1.5 cursor-help underline decoration-dashed decoration-1 underline-offset-[3px] decoration-[hsl(var(--gold))]/60 hover:decoration-[hsl(var(--gold))] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--gold))] rounded-sm"
    >
      {children}
      <span aria-hidden="true" className="sr-only">(inferred from cost)</span>
      {open && (
        <span
          role="tooltip"
          data-testid={`${testid}-tooltip`}
          className="absolute z-50 right-0 top-full mt-1.5 w-56 text-[10px] leading-snug normal-case font-normal tracking-normal text-left px-2 py-1.5 rounded bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] shadow-md text-[hsl(var(--fg))]"
        >
          {COPY}
        </span>
      )}
    </span>
  );
}
